import { SyntheticEvent, useCallback, useRef } from 'react';

import Flex from 'quarks/Flex';

import CheckboxGroup from 'molecules/CheckboxGroup/CheckboxGroup';
import RadioGroup from 'molecules/RadioGroup/RadioGroup';
import Select from 'molecules/Select/Select';
import TextField from 'molecules/TextField/TextField';

import ComponentButton from 'components/Button/ComponentButton';
import { HubSpotField, isFieldVisible } from 'components/Form/HubspotForms/utils';

interface HubspotFormFieldsProps {
  values: { [key: string]: any };
  validation: { [key: string]: boolean };
  fieldGroup: HubSpotField[];
  handleBlur(event: SyntheticEvent): any;
  handleChange(event: SyntheticEvent): any;
}

export const HubspotFormFields = ({
  values,
  validation,
  fieldGroup,
  handleBlur,
  handleChange,
}: HubspotFormFieldsProps) => {
  const removeHiddenFields = (fields: HubSpotField[]) =>
    fields.filter((field: HubSpotField) => {
      const { hidden, filters } = field || {};

      return !(hidden || (!!filters && !isFieldVisible(field, values)));
    });

  const fields = removeHiddenFields(fieldGroup);

  const blurTimeout = useRef(undefined);

  const debouncedHandleBlur = useCallback(
    (event: SyntheticEvent) => {
      if (blurTimeout.current) {
        clearTimeout(blurTimeout.current);
      }

      blurTimeout.current = window.setTimeout(() => {
        handleBlur(event);
      }, 100);
    },
    [handleBlur],
  );

  return (
    fields.length > 0 && (
      <Flex
        data-testid="hs-formfield-group"
        width="100%"
        flexDirection="column"
        gap="0px"
        sm={{ flexDirection: 'row', gap: '16px' }}
      >
        {fields.map((field: any, index: number) => {
          const { name, label, labelHidden, type, fieldType, placeholder, required, defaultValue } = field || {};

          if (type === 'string' || type === 'number') {
            return (
              <TextField
                key={index}
                label={!labelHidden && label}
                type={fieldType === 'phonenumber' ? 'tel' : fieldType}
                name={name}
                value={values[name]}
                defaultValue={defaultValue}
                placeholder={`${placeholder.trim()}${!label && required ? '*' : ''}`}
                required={required}
                width="100%"
                marginBottom={16}
                onChange={handleChange}
                onBlur={debouncedHandleBlur}
                error={validation[name]}
                errorMessage="Please complete this required field."
                css={{
                  maxWidth: '100%',
                  '&& .MuiInputBase-root': {
                    maxWidth: '100%',
                  },
                  '&& .MuiFormHelperText-root': {
                    fontSize: '12px',
                  },
                }}
              />
            );
          }
          if (fieldType === 'select') {
            const { options, unselectedLabel } = field || {};

            return (
              <Select
                key={index}
                label={!labelHidden && label}
                list={options}
                name={name}
                value={values[name]}
                onChange={handleChange}
                onBlur={debouncedHandleBlur}
                placeholderText={unselectedLabel ? `${unselectedLabel.trim()}${required ? '*' : ''}` : label}
                error={validation[name]}
                errorMessage="Please complete this required field."
                width="100%"
                marginBottom={16}
                required={required}
                css={{
                  '&& .MuiInputBase-root': {
                    maxWidth: '100%',
                  },
                }}
              />
            );
          }
          if (fieldType === 'radio' || fieldType === 'booleancheckbox') {
            const { options } = field || {};

            return (
              <RadioGroup
                key={index}
                value={values[name]}
                name={name}
                label={label}
                error={validation[name]}
                errorMessage="Please complete this required field."
                options={options}
                required={required}
                onChange={debouncedHandleBlur}
              />
            );
          }
          if (fieldType === 'checkbox') {
            const { options } = field || {};

            return (
              <CheckboxGroup
                key={index}
                value={values[name]}
                options={options}
                label={label}
                name={name}
                error={validation[name]}
                required={required}
                errorMessage="Please complete this required field."
                onChange={debouncedHandleBlur}
              />
            );
          }

          return null;
        })}
      </Flex>
    )
  );
};

interface PrivacyPolicyProps {
  privacyPolicyHTML: string;
}

export const PrivacyPolicy = ({ privacyPolicyHTML = '' }: PrivacyPolicyProps) =>
  privacyPolicyHTML && (
    <Flex
      fontSize="textSm"
      textColor="gray-700"
      dangerouslySetInnerHTML={{
        __html: privacyPolicyHTML,
      }}
    ></Flex>
  );

interface SubmitProps {
  handleSubmit(): any;
  label: string;
  isSubmitting?: boolean;
}

export const Submit = ({ handleSubmit, label = '', isSubmitting }: SubmitProps) => (
  <Flex
    flexDirection="column"
    width="100%"
    onClick={handleSubmit}
    marginBottom={16}
    css={`
            .MuiButton-root {
            font-size: 1rem;
            max-width: 100% !important;
            min-width: max-content;
            width: 100% !important;
            min-height: 48px !important;
            },
        `}
  >
    <ComponentButton label={label} isButton disabled={isSubmitting} />
  </Flex>
);
